import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  REQUEST_STATUS,
  fetchPopulationMetrics,
  fetchBaselineFollowupMetrics,
  fetchSurveysData,
  setSelectedMetric, canselAllFilters,
} from '../../../../redux_store';
import useChartExport from './hooks/useChartExport';
import { LoadingState, ErrorState, EmptyState, DashboardLayout } from './components';

// Helper function to format date as YYYY-MM-DD in local timezone
const formatLocalDate = (date) => {
  if (!date) {
    return ''
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PopulationHealthDashboard = () => {
  const dispatch = useDispatch();
  const { currentAccountUuid } = useParams();

  // Get data from Redux store
  const {
    metrics,
    totalPatients,
    status,
    error,
    selectedAccount,
    selectedInstrument,
    dateRange,
    isComparisonMode,
    selectedMetric,
  } = useSelector((state) => state.populationHealth);

  // Modal state (keep local since it's UI state)
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Track if surveys have been loaded
  const surveysLoadedRef = useRef(false);

  // Chart export functionality
  const chartRef = useRef(null);
  const exportOptions = {
    title: "Population Health Assessment Report",
    subtitle: "Comprehensive patient outcome analysis and clinical metrics overview",
    dateRange,
    selectedInstrument,
    isComparisonMode,
    totalPatients,
  };

  const {
    handleDataExport,
    handleChartExport,
    isPngExporting,
    isPdfExporting,
    isDataExporting
  } = useChartExport(chartRef, currentAccountUuid, exportOptions);

  // Load surveys when account changes
  useEffect(() => {
    // Use selected account UUID if available, otherwise use current account from params
    const accountUuid = selectedAccount?.uuid || currentAccountUuid;

    if (accountUuid) {
      console.log('Loading surveys for account:', accountUuid);
      dispatch(fetchSurveysData({ accountUuid: accountUuid }));
    }
  }, [dispatch, currentAccountUuid, selectedAccount]);

  // Load data when filters change
  useEffect(() => {
    // Use selected account UUID if available, otherwise use current account from params
    const accountUuid = selectedAccount?.uuid || currentAccountUuid;

    if (accountUuid) {
      let filters = {
        assessmentType: selectedInstrument === 'all' ? undefined : selectedInstrument,
      };

      if (dateRange.startDate && dateRange.endDate) {
        filters = {
          ...filters,
          startDate: formatLocalDate(dateRange.startDate),
          endDate: formatLocalDate(dateRange.endDate),
        };
      }

      // Use different endpoint based on comparison mode
      if (isComparisonMode) {
        dispatch(fetchBaselineFollowupMetrics({
          accountUuid: accountUuid,
          filters
        }));
      } else {
        dispatch(fetchPopulationMetrics({
          accountUuid: accountUuid,
          filters
        }));
      }
    }
  }, [dispatch, currentAccountUuid, selectedAccount, selectedInstrument, dateRange, isComparisonMode]);

  useEffect(() => {
    return () => dispatch(canselAllFilters());
  }, []);

  const handleMetricClick = (metric) => {
    dispatch(setSelectedMetric(metric));
    setIsModalOpen(true);
  };

  const handleExport = async () => {
    const filters = {
      startDate: formatLocalDate(dateRange.startDate),
      endDate: formatLocalDate(dateRange.endDate),
      assessmentType: selectedInstrument === 'all' ? undefined : selectedInstrument,
    };
    await handleDataExport(filters, isComparisonMode);
  };


  // Render based on Redux state
  const loading = status === REQUEST_STATUS.loading;
  const hasError = status === REQUEST_STATUS.failed;
  const isEmpty = metrics.length === 0;

  // Common props for state components
  const stateProps = {
    onExport: handleExport,
    onExportChart: handleChartExport,
    // Loading states
    isPngExporting,
    isPdfExporting,
    isDataExporting,
    hasError,
  };

  // Dashboard layout props
  const dashboardProps = {
    ...stateProps,
    metrics,
    onMetricClick: handleMetricClick,
    chartRef,
    selectedMetric,
    isModalOpen,
    hasError,
    onModalClose: () => setIsModalOpen(false),
  };

  if (loading) {
    return <LoadingState {...stateProps} />;
  }

  if (isEmpty) {
    return <EmptyState {...stateProps} />;
  }

  return <DashboardLayout {...dashboardProps} />;
};

export default PopulationHealthDashboard;
